/**
 * Account Service
 *
 * Service for account management operations: profile, preferences, security
 */

import { useAuthStore } from '@vsat/shared/store/authStore';

const getApiBaseUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3001/api/v1';
  }
  return `${window.location.origin}/api/v1`;
};

const API_BASE = `${getApiBaseUrl()}/auth`;
const CAREER_ORIENTATION_BASE = `${getApiBaseUrl()}/career-orientation`;

export interface UserProfile {
  id: string;
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  dateOfBirth?: string;
  school?: string;
  gradeLevel?: string;
  address?: string;
  province?: string; // @deprecated
  district?: string; // @deprecated
  provinceCode?: string;
  provinceName?: string;
  communeCode?: string;
  communeName?: string;
}

export interface UserPreferences {
  language: string;
  emailNotifications: boolean;
  smsNotifications: boolean;
  updatedAt: string;
}

export interface SecurityStatus {
  lastPasswordChange: string | null;
  passwordStrength: string;
}

export interface UpdateProfileData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  dateOfBirth?: string;
  gradeLevel?: string;
  school?: string;
  address?: string;
  province?: string; // @deprecated
  district?: string; // @deprecated
  provinceCode?: string;
  provinceName?: string;
  communeCode?: string;
  communeName?: string;
}

export interface UpdatePreferencesData {
  language?: string;
  emailNotifications?: boolean;
  smsNotifications?: boolean;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface AssessmentSummary {
  id: string;
  date: string;
  status: 'completed' | 'in-progress';
  educationLevel?: string;
}

class AccountService {
  /**
   * Handle 401 errors by clearing auth and redirecting to login
   */
  private async handleUnauthorized() {
    // Call logout from auth store to clear all state
    const { logout } = useAuthStore.getState();
    await logout();
    // Redirect to login
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  /**
   * Get user profile
   */
  async getProfile(): Promise<UserProfile> {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      throw new Error('Failed to fetch profile');
    }

    return response.json();
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateProfileData): Promise<UserProfile> {
    const response = await fetch(`${API_BASE}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      body: JSON.stringify(data),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update profile');
    }

    return response.json();
  }

  /**
   * Get user preferences
   */
  async getPreferences(): Promise<UserPreferences> {
    const response = await fetch(`${API_BASE}/preferences`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      throw new Error('Failed to fetch preferences');
    }

    return response.json();
  }

  /**
   * Update user preferences
   */
  async updatePreferences(data: UpdatePreferencesData): Promise<UserPreferences> {
    const response = await fetch(`${API_BASE}/preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      body: JSON.stringify(data),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to update preferences');
    }

    return response.json();
  }

  /**
   * Get security status
   */
  async getSecurityStatus(): Promise<SecurityStatus> {
    const response = await fetch(`${API_BASE}/security-status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      throw new Error('Failed to fetch security status');
    }

    return response.json();
  }

  /**
   * Change password
   */
  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await fetch(`${API_BASE}/change-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      body: JSON.stringify(data),
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to change password');
    }

    return response.json();
  }

  /**
   * Get recent assessments
   */
  async getRecentAssessments(limit: number = 10): Promise<AssessmentSummary[]> {
    const response = await fetch(`${CAREER_ORIENTATION_BASE}/assessments?limit=${limit}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (response.status === 401) {
      this.handleUnauthorized();
      throw new Error('Unauthorized - Please login again');
    }

    if (!response.ok) {
      throw new Error('Failed to fetch assessments');
    }

    const result = await response.json();
    return result.data || [];
  }
}

export const accountService = new AccountService();
